/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * QuestionsScreen.tsx - Screen for showing questions one after another for a duration
 */

import { useAudioPlayer } from 'expo-audio';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCallbackRef } from '../../hooks/useMemoRef';
import useRefLazy from '../../hooks/useRefLazy';
import useSettingsStore from '../../hooks/useSettingsStore';
import { getCurrentRoute } from '../../navigation/helpers';
import { RootStackProps } from '../../navigation/types';
import QuestionComponent from './QuestionComponent';
import { getStoreForCategory } from './QuestionsStore';
import { formatQuestion } from './QuestionStatement/QuestionStatementString';
import { Question, Result } from './types';
import { SafeAreaView } from 'react-native-safe-area-context';
import { StyleSheet } from 'react-native';

/**
 * Screen component for showing a question to the user along with somewhere to input their answer.
 *
 * This component is responsible for:
 * - obtaining new questions, and passing them by props to relevant components to render
 * - avoiding the user seeing the same question in quick succession
 * - listening for questions being answered and moving to the next question
 * - tracking the user's answers
 * - managing a timer and moving onto the results screen
 * - loading and unloading the sound files (so we do it in one place, rather than once per question)
 *
 * In short, this component is responsible for thinking about a set of questions. Per-question thinking is done by
 * sub-components.
 *
 * This component does not necessarily support the question category changing under its feet, nor does it necessarily
 * support being used after the timer has ended. This means that each time the user wishes to start a new session of
 * questions (restarting the timer and clearing their results), this screen should be navigated to in a way that
 * ensures a new component is created (e.g. with a new key, or by ensuring that this screen isn't already on the
 * stack).
 */
const QuestionsScreen = ({ route, navigation }: RootStackProps<'Questions'>) => {
  const category = route.params;

  const questionStore = useRefLazy(() => getStoreForCategory(category.name));
  const [question, setQuestion] = useState(() => questionStore.current.getQuestion());
  const results = useRef<Result[]>([]);
  const [score, setScore] = useState(0);

  const audioEnabled = useSettingsStore(settings => settings.audioEnabled);
  
  const correctPlayer = useAudioPlayer(require('../../assets/correct.m4a'));
  const wrongPlayer = useAudioPlayer(require('../../assets/wrong.m4a'));

  const questionStartTime = useRefLazy<number>(() => Date.now());
  const isQuestionInResults = useRef(false);

  const nextQuestionDelayTimer = useRef<NodeJS.Timer>(undefined);

  // Start one minute timer to navigate to results screen.
  // We use useCallbackRef so that we can update the callback as things change, without having to restart the timer.
  const navigateToResultsScreen = useCallbackRef(() => {
    // If the user has seen this question for at least 5 seconds, add an unanswered question to the results.
    if (!isQuestionInResults.current && Date.now() > questionStartTime.current + 5*1000) {
      const result: Result = {
        question: formatQuestion(question),
        answer: question.answer,
        correct: false
      };
      results.current.push(result);
    }

    // Remove ourselves and navigate to the results screen.
    if (getCurrentRoute(navigation).name !== route.name) {
      // We've hit an edge-case where the user has navigated away but the timer hasn't been cancelled yet.
      // Don't navigate anywhere
    } else {
      navigation.replace('Results', {
        score: score,
        category,
        results: results.current,
      });
    }
  }, [category, route, navigation, question, questionStartTime, score]);
  useEffect(() => {
    const timer = setTimeout(() => navigateToResultsScreen.current(), 60*1000);
    return () => clearTimeout(timer);
  }, [navigateToResultsScreen]);


  // Callback function for children to play sound
  const playSound = useCallback((isCorrect: boolean) => {
    if (audioEnabled) {
      const player = isCorrect ? correctPlayer : wrongPlayer;
      player.seekTo(0);
      player.play();
    }
  }, [audioEnabled, correctPlayer, wrongPlayer]);

  // Callback function for children to notify us that the question is answered
  const questionAnswered = useCallback((isCorrect: boolean, userAnswer?: Question['answer']) => {
    const result: Result = {
      question: formatQuestion(question),
      answer: question.answer,
      userAnswer: userAnswer,
      correct: isCorrect
    };

    // Immediately add the result
    results.current.push(result);
    isQuestionInResults.current = true;
    if (result.correct === true) {
      setScore((prev) => prev+1);
    }

    // In 0.4s, go to the next question
    nextQuestionDelayTimer.current = setTimeout(() => {
      setQuestion(
        // Do a shallow copy by using the spread operator (i.e ...).
        // This ensures that this question is guaranteed to be a new object and will fail identity checks (i.e. ===)
        // with the old question. This is important because QuestionComponent uses the question identity changing to
        // trigger a reset.
        { ...questionStore.current.getQuestion() }
      );
      isQuestionInResults.current = false;
      questionStartTime.current = Date.now();
    }, 0.4*1000);
  }, [question, questionStartTime, questionStore]);

  useEffect(() => {
    // Clear the timer (used above) if we unmount.
    return () => nextQuestionDelayTimer.current && clearTimeout(nextQuestionDelayTimer.current);
  }, []);

  return (
    <SafeAreaView style={styles.container} edges={['right', 'left', 'bottom']}>
      <QuestionComponent question={question} playSound={playSound} questionAnswered={questionAnswered}/>
    </SafeAreaView>
  );
};

export default QuestionsScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  }
});
